import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
}

interface ConfirmState extends ConfirmOptions {
  open: boolean;
  resolve: (value: boolean) => void;
}

const ConfirmContext = React.createContext<(opts: ConfirmOptions) => Promise<boolean>>(
  () => {
    throw new Error('ConfirmProvider missing');
  },
);

export function ConfirmProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, setState] = React.useState<ConfirmState | null>(null);

  const confirm = React.useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setState({ ...opts, open: true, resolve });
      }),
    [],
  );

  const close = (value: boolean): void => {
    state?.resolve(value);
    setState((s) => (s ? { ...s, open: false } : null));
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <Dialog open={state.open} onOpenChange={(o) => !o && close(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{state.title}</DialogTitle>
              {state.description && (
                <DialogDescription>{state.description}</DialogDescription>
              )}
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => close(false)}>
                {state.cancelLabel ?? 'Cancelar'}
              </Button>
              <Button
                variant={state.variant === 'danger' ? 'destructive' : 'default'}
                onClick={() => close(true)}
              >
                {state.confirmLabel ?? 'Confirmar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  return React.useContext(ConfirmContext);
}
